import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Chip,
  Paper,
  IconButton,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import UndoIcon from '@mui/icons-material/Undo';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import { apiFetch, openAuthenticatedUpload } from '../../api/client';

const ACAO_BOTOES = [
  {
    value: 'DEVOLVER',
    label: 'Devolver',
    hint: 'Volta uma etapa',
    icon: <UndoIcon fontSize="small" />,
    color: '#ea580c',
    bg: 'rgba(234,88,12,0.08)',
    border: 'rgba(234,88,12,0.35)',
  },
  {
    value: 'REJEITAR',
    label: 'Rejeitar',
    hint: 'Retorna ao início',
    icon: <ThumbDownIcon fontSize="small" />,
    color: '#dc2626',
    bg: 'rgba(220,38,38,0.08)',
    border: 'rgba(220,38,38,0.35)',
  },
  {
    value: 'AUTORIZAR',
    label: 'Autorizar',
    hint: 'Avança o fluxo de pagamento',
    icon: <ThumbUpIcon fontSize="small" />,
    color: '#059669',
    bg: 'rgba(5,150,105,0.1)',
    border: 'rgba(5,150,105,0.4)',
  },
];

/**
 * Painel de Decisão para atividades de processo de pagamento (NF).
 * Espelha o UX do AtualizarProcessoModal, vinculado às etapas do fluxo Pagamento.
 */
export default function PainelDecisaoPagamentoModal({
  open,
  onClose,
  atividade,
  pdfBlobUrl,
  loadingPdf,
  podeDecidir,
  enviando,
  onSubmit,
  onContinuarAssinatura,
  userEmail,
}) {
  const [fluxo, setFluxo] = useState(null);
  const [loadingFluxo, setLoadingFluxo] = useState(false);
  const [acao, setAcao] = useState('AUTORIZAR');
  const [etapaDestinoIndex, setEtapaDestinoIndex] = useState('');
  const [observacao, setObservacao] = useState('');
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAcao('AUTORIZAR');
    setObservacao('');
    setAttempted(false);
    setLoadingFluxo(true);
    apiFetch('/api/contratos/fluxos')
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        const lista = Array.isArray(data) ? data : data?.fluxos || [];
        const pag = lista.find((f) => String(f?.nome || '').toLowerCase().includes('pagamento')) || null;
        setFluxo(pag);
        const etapas = Array.isArray(pag?.etapas) ? [...pag.etapas].sort((a, b) => (a.ordem || 0) - (b.ordem || 0)) : [];
        const atual = Number(atividade?.etapaAtual ?? 0);
        const proximo = Math.min(atual + 1, Math.max(etapas.length - 1, 0));
        setEtapaDestinoIndex(String(proximo));
      })
      .catch(() => setFluxo(null))
      .finally(() => setLoadingFluxo(false));
  }, [open, atividade?.id, atividade?.etapaAtual]);

  const etapas = useMemo(() => {
    const list = Array.isArray(fluxo?.etapas) ? [...fluxo.etapas] : [];
    return list.sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
  }, [fluxo]);

  const etapaAtualLabel = useMemo(() => {
    const idx = Number(atividade?.etapaAtual ?? 0);
    return etapas[idx]?.nome || `Etapa ${idx + 1}`;
  }, [atividade?.etapaAtual, etapas]);

  const destinos = useMemo(() => {
    if (!etapas.length) return [];
    if (acao === 'REJEITAR') return [{ index: 0, nome: etapas[0]?.nome || 'Início' }];
    if (acao === 'DEVOLVER') {
      const atual = Number(atividade?.etapaAtual ?? 0);
      const dest = Math.max(0, atual - 1);
      return [{ index: dest, nome: etapas[dest]?.nome || `Etapa ${dest + 1}` }];
    }
    return etapas.map((e, index) => ({ index, nome: e.nome, exigeAssinatura: e.exigeAssinatura !== false && e.exige_assinatura !== 0 }));
  }, [acao, etapas, atividade?.etapaAtual]);

  useEffect(() => {
    if (!destinos.length) return;
    if (acao === 'AUTORIZAR') {
      const atual = Number(atividade?.etapaAtual ?? 0);
      const prox = destinos.find((d) => d.index === atual + 1) || destinos[destinos.length - 1];
      setEtapaDestinoIndex(String(prox?.index ?? 0));
    } else {
      setEtapaDestinoIndex(String(destinos[0].index));
    }
  }, [acao, destinos, atividade?.etapaAtual]);

  const handleConfirm = async () => {
    setAttempted(true);
    const obs = String(observacao || '').trim();
    if (!obs) return;
    await onSubmit?.({
      acao,
      observacao: obs,
      etapaDestinoIndex: Number(etapaDestinoIndex),
      usuarioEmail: userEmail,
    });
  };

  const obsOk = String(observacao || '').trim().length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #0d47a1 0%, #1976d2 100%)',
          color: 'white',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 1 }}>
            PAINEL DE DECISÃO
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            Atualizar Processo de Pagamento
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.95, mt: 0.5 }}>
            {atividade?.numeroProcesso || atividade?.numero_processo} · {atividade?.empresa || '—'}
          </Typography>
          <Stack direction="row" gap={1} sx={{ mt: 1 }} flexWrap="wrap">
            <Chip
              size="small"
              label={atividade?.statusLabel || atividade?.status || '—'}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700 }}
            />
            <Chip
              size="small"
              label={`Agora: ${etapaAtualLabel}`}
              sx={{ bgcolor: 'rgba(255,255,255,0.85)', color: '#0d47a1', fontWeight: 700 }}
            />
          </Stack>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            {loadingPdf ? (
              <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress />
              </Box>
            ) : pdfBlobUrl ? (
              <iframe
                title="PDF consolidado"
                src={pdfBlobUrl}
                style={{ width: '100%', height: 520, border: '1px solid #e0e0e0', borderRadius: 8 }}
              />
            ) : (
              <Alert severity="info">PDF consolidado não disponível.</Alert>
            )}
          </Grid>

          <Grid item xs={12} md={5}>
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Contratos para vistoria
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<DescriptionOutlinedIcon />}
                  disabled={!atividade?.contratoPath}
                  onClick={() => atividade?.contratoPath && openAuthenticatedUpload(atividade.contratoPath)}
                >
                  {atividade?.numeroContrato
                    ? `Ver contrato ${atividade.numeroContrato}`
                    : 'Ver contrato cadastrado'}
                </Button>
                {atividade?.status === 'EM_ANDAMENTO' &&
                  (atividade?.avaliacaoAssinaturaDocumentoId || atividade?.assinaturaDocumentoId) && (
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<DrawOutlinedIcon />}
                      sx={{ mt: 1.5, textTransform: 'none' }}
                      onClick={() => onContinuarAssinatura?.(atividade)}
                    >
                      Continuar assinatura digital
                    </Button>
                  )}
              </Paper>

              {!podeDecidir ? (
                <Alert severity="info">
                  {atividade?.status === 'PENDENTE'
                    ? 'Apenas o fiscal do setor pode registrar a decisão neste painel.'
                    : 'Este processo não está pendente de decisão fiscal.'}
                </Alert>
              ) : (
                <>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      1 — Destino
                    </Typography>
                    {loadingFluxo ? (
                      <CircularProgress size={22} />
                    ) : (
                      <FormControl fullWidth size="small">
                        <InputLabel>Nova etapa</InputLabel>
                        <Select
                          label="Nova etapa"
                          value={etapaDestinoIndex}
                          onChange={(e) => setEtapaDestinoIndex(e.target.value)}
                        >
                          {destinos.map((d) => (
                            <MenuItem key={d.index} value={String(d.index)}>
                              {d.index + 1}. {d.nome}
                              {d.exigeAssinatura ? ' (assinatura)' : ''}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      2 — Ação
                    </Typography>
                    <Stack spacing={1}>
                      {ACAO_BOTOES.map((btn) => {
                        const ativo = acao === btn.value;
                        return (
                          <Button
                            key={btn.value}
                            fullWidth
                            onClick={() => setAcao(btn.value)}
                            startIcon={btn.icon}
                            sx={{
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                              color: btn.color,
                              bgcolor: ativo ? btn.bg : 'transparent',
                              border: `1px solid ${ativo ? btn.border : 'rgba(0,0,0,0.12)'}`,
                              fontWeight: ativo ? 700 : 500,
                            }}
                          >
                            <Box textAlign="left">
                              <Typography variant="body2" fontWeight={700}>
                                {btn.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {btn.hint}
                              </Typography>
                            </Box>
                          </Button>
                        );
                      })}
                    </Stack>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      3 — Observação
                    </Typography>
                    <TextField
                      fullWidth
                      required
                      multiline
                      minRows={3}
                      placeholder="Descreva a decisão"
                      helperText={
                        attempted && !obsOk
                          ? 'Obrigatória para registrar auditoria da decisão'
                          : 'Obrigatória para registrar auditoria da decisão'
                      }
                      error={attempted && !obsOk}
                      value={observacao}
                      onChange={(e) => setObservacao(e.target.value)}
                    />
                  </Paper>
                </>
              )}
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose}>Cancelar</Button>
        {podeDecidir && (
          <Button
            variant="contained"
            startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : <EditIcon />}
            disabled={enviando || !obsOk}
            onClick={handleConfirm}
            sx={{ textTransform: 'none' }}
          >
            Atualizar Processo
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
